import {
  Children,
  cloneElement,
  type FocusEvent,
  type MouseEvent,
  type ReactElement,
  useState,
} from 'react';

import { Tooltip, type TooltipProps } from '@/components/common/tooltip';

type TriggerProps = {
  onMouseEnter?: (e: MouseEvent<HTMLElement>) => void;
  onFocus?: (e: FocusEvent<HTMLElement>) => void;
};

function overflows(el: Element) {
  return el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
}

/** Cut off by an ellipsis or a line clamp — on the element itself or on any line inside it (an
 *  address rendered as a street line over a locality line). */
function isTruncated(el: Element) {
  return overflows(el) || Array.from(el.querySelectorAll('*')).some(overflows);
}

export type OverflowTooltipProps = Omit<TooltipProps, 'children'> & {
  children: ReactElement<TriggerProps>;
};

/**
 * A tooltip carrying the full value of text that CSS truncates, shown **only while the text is
 * actually cut off** — a tooltip repeating text already fully on screen is noise, and a screen
 * reader announces it twice. It is the rule antd's own `Typography` `ellipsis.tooltip` and the
 * common design systems follow.
 *
 * Measured at the moment of hover/focus rather than kept current with a `ResizeObserver`: the
 * answer is only wanted then, so nothing runs for the hundreds of cells that are never hovered.
 * The child keeps its own element and class; its `onMouseEnter`/`onFocus` still run.
 */
export function OverflowTooltip({ title, children, ...rest }: OverflowTooltipProps) {
  const [truncated, setTruncated] = useState(false);
  const child = Children.only(children);

  return (
    <Tooltip {...rest} title={truncated ? title : undefined}>
      {cloneElement(child, {
        onMouseEnter: (e: MouseEvent<HTMLElement>) => {
          setTruncated(isTruncated(e.currentTarget));
          child.props.onMouseEnter?.(e);
        },
        onFocus: (e: FocusEvent<HTMLElement>) => {
          setTruncated(isTruncated(e.currentTarget));
          child.props.onFocus?.(e);
        },
      })}
    </Tooltip>
  );
}
